import { useEffect, useMemo } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getAgentActivity,
  getProjectHistory,
  getProjects,
  type ProjectMessage,
  type ProjectRow,
} from '../transport/api'
import { useIdleStatus } from '../agents/useIdleStatus'
import { dmAsRooms } from '../chat/dmUnread'
import { listConversations } from '../chatv1/transport'
import { onChatv1Frame } from '../chatv1/ws'
import {
  ACTIVITY_MAX,
  ACTIVITY_PAGE,
  busActivity,
  inboxActivity,
  mergeActivity,
  projectActivity,
  saidActivity,
  type ActivityItem,
} from './feed'

/**
 * The Activity feed's data.
 *
 * Deliberately NOT a copy of useProjectMessages: that hook owns ONE room and holds a
 * resume cursor for it, which is the right shape for a transcript you scroll and the
 * wrong one for a feed that only ever wants each room's newest page. Sharing it would
 * have meant N cursors and N reconcile loops to render a list that never pages
 * backwards.
 *
 * Three things it does share, because each was learned the expensive way there:
 *   - `/project/events` is a stream of EVERY room (the server has one queue list and
 *     no per-project subscription), so one EventSource covers the whole feed. The
 *     panel filters nothing; it files each frame under its own project_id.
 *   - `ready` fires on the initial open AND on every silent EventSource reconnect, so
 *     it is the reconcile trigger — anything sent while the stream was down is
 *     otherwise invisible.
 *   - a foreground transition can never be trusted to have kept the stream alive.
 *
 * Cost when the panel is closed: zero. Every query is gated on `enabled`, and the
 * stream is only opened while it is mounted.
 */
export function useActivity(enabled: boolean, selfEmail: string | null) {
  const qc = useQueryClient()

  /*
   * NOT VAULT-GATED — owner, 2026-08-22: 在 Activity Tab 的彈出視窗裡面現在所有
   * project 的未讀訊息都沒有辦法顯示在裡面.
   *
   * `enabled: unlocked` is the vault-era leftover already removed from the Friends
   * tab and from the rail's own Projects section, reaching its third component. On
   * this deployment /security/status answers `{}`, so vaultOpen() is false and this
   * query — and the histories below — never ran at all. Project Chat is a CLOUD service
   * with its own token and does not depend on the machine vault.
   *
   * This used to be `projectOnDynamo() || unlocked`, waiting on the vault for the edge
   * lane's sake. The edge lane was deleted on 2026-09-02 (owner: Project Chat 舊的路徑
   * 完全刪了), so the gate is gone and the hook no longer takes `unlocked` at all.
   */
  // Same key as the sidebar and App, so this is the cache entry they already keep warm.
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    retry: 3,
  })
  /*
   * FRIEND DMs ARE ROOMS HERE — owner, 2026-08-22: Project 那個欄位應該可以改名為
   * Chat 這樣也包含 friend 1-1 chat.
   *
   * Same query key App uses, so this is a second reader of a warm cache entry, not a
   * second fetch. Adapted into ProjectRow (chat/dmUnread dmAsRooms) so the feed keeps
   * ONE renderer, one unread rule and one history lane for every conversation — the
   * alternative, a parallel DM path through this hook, is two places to fix the next
   * thing and two places to forget.
   */
  const dmsQ = useQuery({
    queryKey: ['dm-conversations'],
    queryFn: () => listConversations('dm'),
    retry: 3,
  })
  const projects: ProjectRow[] = useMemo(
    () => [...(projectsQ.data?.projects ?? []), ...dmAsRooms(dmsQ.data?.conversations)],
    [projectsQ.data, dmsQ.data],
  )

  /**
   * The newest page of every room, in parallel.
   *
   * `limit` with no `after` is the server's newest-N branch. Without it this would be
   * every message of every project on every open — the same whole-history request the
   * transcript view was just cured of, multiplied by the number of rooms.
   */
  const histories = useQueries({
    queries: projects.map((p) => ({
      queryKey: ['activity-history', p.project_id],
      queryFn: () => getProjectHistory(p.project_id, undefined, ACTIVITY_PAGE),
      enabled,
      // The SSE stream is what keeps this current; the interval is a backstop for a
      // stream that died without saying so.
      staleTime: 15_000,
    })),
  })

  const byProject = useMemo(() => {
    const m = new Map<string, ProjectMessage[]>()
    projects.forEach((p, i) => {
      const rows = histories[i]?.data?.messages
      if (rows?.length) m.set(p.project_id, rows)
    })
    return m
  }, [projects, histories])

  const idle = useIdleStatus()

  /**
   * The agent bus log. Polled rather than streamed — there is no SSE for it, and at
   * 15s a feed that is open on screen stays current without adding a third
   * long-lived connection alongside /project/events and the terminal socket.
   *
   * `retry: false` and no error surfaced: an edge older than 74fc393 has no such
   * endpoint, and that is a fact about the deployment rather than a failure. The
   * feed simply falls back to the per-agent rows, which is what it had before.
   */
  const busQ = useQuery({
    queryKey: ['agent-activity'],
    queryFn: () => getAgentActivity(ACTIVITY_MAX),
    enabled,
    retry: false,
  })

  const { items, truncated } = useMemo(() => {
    const roster = [...idle.byKey.values()]
    const merged = mergeActivity(
      projectActivity(projects, byProject, selfEmail),
      // Bus events BEFORE the roster's per-agent rows: both emit the same key for
      // the same delivery (they are written from one timestamp server-side) and
      // mergeActivity keeps the first, so the copy that names the sender wins.
      busActivity(busQ.data?.events ?? []),
      inboxActivity(roster),
      saidActivity(roster),
    )
    return {
      items: merged.slice(0, ACTIVITY_MAX) as ActivityItem[],
      // Said out loud rather than left as a silent cut — a list that stops at a
      // round number without saying so reads as "that is everything there is".
      truncated: merged.length > ACTIVITY_MAX,
    }
  }, [projects, byProject, idle.byKey, busQ.data, selfEmail])

  /* ── live, on the chat backend ─────────────────────────────────────────── */
  /*
   * A message in ANY room changes this feed, and the count that decides which rows
   * are unread lives on the project ROW, not on the message — so a frame invalidates
   * that room's page and the list, and lets the queries say what is true.
   *
   * The socket is already open (chatv1/bootstrap runs at sign-in), so this costs one
   * subscription and no connection. It replaces the EventSource below rather than
   * joining it: `/project/events` is an EDGE route, and on this backend it does not
   * exist — the browser would retry a 404 forever with backoff, for a stream that can
   * never carry anything.
   */
  useEffect(() => {
    if (!enabled) return
    return onChatv1Frame((f) => {
      if (f.type === 'message') {
        void qc.invalidateQueries({ queryKey: ['activity-history', f.conv_id] })
        void qc.invalidateQueries({ queryKey: ['projects'] })
        void qc.invalidateQueries({ queryKey: ['dm-conversations'] })
      } else if (f.type === 'projects_changed') {
        void qc.invalidateQueries({ queryKey: ['projects'] })
        void qc.invalidateQueries({ queryKey: ['dm-conversations'] })
      }
    })
  }, [enabled, qc])


  return {
    items,
    truncated,
    loading: enabled && histories.some((h) => h.isPending),
    /** Rooms whose page could not be loaded — named, not silently missing. */
    failed: projects.filter((_p, i) => histories[i]?.isError).map((p) => p.name ?? p.project_id),
    /**
     * The authoritative unread total (see feed.ts activityUnread). Kept beside the
     * rows so the panel can say "3 unread, 1 of them older than this page" instead
     * of quietly showing fewer marks than the badge promised.
     */
    marked: items.reduce((n, i) => n + (i.unread ? 1 : 0), 0),
  }
}
